import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, unref as _unref, resolveDirective as _resolveDirective, openBlock as _openBlock, createElementBlock as _createElementBlock, withDirectives as _withDirectives, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = (n: any) => (_pushScopeId("data-v-8ebd0102"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "drawer-body" }
const _hoisted_2 = { class: "section-title-row" }
const _hoisted_3 = { class: "section-title" }
const _hoisted_4 = { class: "table-wrap" }
const _hoisted_5 = { class: "cm-align-center gap-x-5" }
const _hoisted_6 = { class: "chart-group" }

import { ref, reactive, watch, computed } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { EChartsOption, getInstanceByDom, init } from 'echarts'
import { applyEchartsFontToOption } from '@/utils/echarts-option/font'
import { ECHARTS_LINE_COLOR, withLineSeriesStyle } from '@/utils/echarts-option/line'
import CmTable from '@/components/cmTable/index.vue'
import { TableOpt } from '@/components/cmTable/types'
import { bytesToSize } from '@/utils/tool'
import { FlowService } from '@/views/dashboard/manage/terminal-traffic-control/terminal-traffic-state-list/service'
import { DeviceStatusService } from '@/views/dashboard/safe-status/device-status/device-status.service'
import type { DeviceFlow } from '@/views/dashboard/safe-status/device-status/device-status.service'
import { useI18n } from 'vue-i18n'

export interface TrafficDetailDevice {
  device_id: string
  computer_name?: string
}


export default /*#__PURE__*/_defineComponent({
  __name: 'traffic-detail-drawer',
  props: {
    modelValue: { type: Boolean },
    device: { default: null }
  },
  emits: ["update:modelValue"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

const { t, locale } = useI18n()

const injector = useReflectiveInjector([FlowService, DeviceStatusService])
const flowService = injector.get(FlowService)
const deviceStatusService = injector.get(DeviceStatusService)

const getSafeMonitorFlowStatTopApi = flowService.getSafeMonitorFlowStatTop.bind(flowService)
const getDeviceFlowDetailApi = deviceStatusService.getDeviceFlowDetail.bind(deviceStatusService)

const drawerTitle = computed(() => {
  void locale.value
  const name = props.device?.computer_name || t('overviewZone.trafficTerminalDefault')
  return t('overviewZone.trafficDetailTitle', { name })
})

const queryForm = reactive({
  data_type: 1 as 1 | 2,
  device_id: '',
  limit: 5
})

const tableOpt = computed<TableOpt>(() => ({
  bodyStyle: 'background: transparent;padding:0',
  containerOpt: { style: 'padding: 0 0;background: var(--cm-color-card-bg)' },
  path: 'data',
  hiddenPageTool: true,
  showActionBar: false,
  showForm: false,
  showRightToolbar: false,
  rowKey: 'device_id',
  selection: false,
  columns: [
    { label: t('overviewZone.trafficAppName'), prop: 'name' },
    { label: t('overviewZone.trafficDescription'), prop: 'description' },
    {
      label:
        queryForm.data_type === 1
          ? t('overviewZone.trafficUploadCol')
          : t('overviewZone.trafficDownloadCol'),
      prop: 'num',
      width: 140,
    },
  ],
}))

const tableRef = ref()
const { refreshList } = useCmTableListRefresh(tableRef)
const networkSpeedChartEl = ref<HTMLCanvasElement>()
const flowChartEl = ref<HTMLCanvasElement>()
const loading = ref(false)

watch(
  () => props.device,
  (device) => {
    if (device?.device_id) {
      queryForm.device_id = device.device_id
    }
  },
  { immediate: true }
)

function changeTab() {
  void refreshList()
}

function buildTrafficLineSeries (
  data: DeviceFlow[],
  field: 'up' | 'down',
  name: string,
  color: string,
) {
  return withLineSeriesStyle({
    data: data.map((i) => i[field]),
    type: 'line',
    smooth: true,
    sampling: 'average',
    name,
  }, color)
}

function drawNetworkSpeedChart(data: DeviceFlow[]) {
  if (!networkSpeedChartEl.value) return
  Object.assign(networkSpeedChartEl.value, {
    width: networkSpeedChartEl.value.offsetWidth,
    height: networkSpeedChartEl.value.offsetHeight
  })
  getInstanceByDom(networkSpeedChartEl.value)?.dispose()
  const chart = init(networkSpeedChartEl.value, null, { devicePixelRatio: 1 })
  const upName = t('overviewZone.trafficChartSeriesUpload')
  const downName = t('overviewZone.trafficChartSeriesDownload')
  const option: EChartsOption = {
    tooltip: { trigger: 'axis' },
    grid: { top: 60, left: 0, right: 20, bottom: 26, containLabel: true },
    title: { text: t('overviewZone.trafficChartNetworkSpeedTitle'), textStyle: { fontSize: 14 } },
    legend: { data: [upName, downName], right: 0, align: 'right', textStyle: { fontSize: 14 } },
    xAxis: { type: 'category', data: data.map((i) => i.time) },
    yAxis: {
      type: 'value',
      name: t('overviewZone.trafficChartYAxisKbps'),
      nameTextStyle: { padding: [0, 0, 0, 40] as unknown as number }
    },
    series: [
      buildTrafficLineSeries(data, 'up', upName, ECHARTS_LINE_COLOR.upload),
      buildTrafficLineSeries(data, 'down', downName, ECHARTS_LINE_COLOR.download),
    ],
  }
  chart.setOption(applyEchartsFontToOption(option as Record<string, unknown>))
}

function drawFlowChart(data: DeviceFlow[]) {
  if (!flowChartEl.value) return
  Object.assign(flowChartEl.value, {
    width: flowChartEl.value.offsetWidth,
    height: flowChartEl.value.offsetHeight
  })
  getInstanceByDom(flowChartEl.value)?.dispose()
  const chart = init(flowChartEl.value, null, { devicePixelRatio: 1 })
  const upName = t('overviewZone.trafficChartSeriesUpload')
  const downName = t('overviewZone.trafficChartSeriesDownload')
  const option: EChartsOption = {
    tooltip: { trigger: 'axis' },
    grid: { top: 60, left: 0, right: 20, bottom: 26, containLabel: true },
    title: { text: t('overviewZone.trafficChartFlowSevenDaysTitle'), textStyle: { fontSize: 14 } },
    legend: { data: [upName, downName], right: 0, align: 'right', textStyle: { fontSize: 14 } },
    xAxis: { type: 'category', data: data.map((i) => i.time) },
    yAxis: {
      type: 'value',
      name: t('overviewZone.trafficChartYAxisMb'),
      nameTextStyle: { padding: [0, 0, 0, 20] as unknown as number }
    },
    series: [
      buildTrafficLineSeries(data, 'up', upName, ECHARTS_LINE_COLOR.upload),
      buildTrafficLineSeries(data, 'down', downName, ECHARTS_LINE_COLOR.download),
    ],
  }
  chart.setOption(applyEchartsFontToOption(option as Record<string, unknown>))
}

async function loadCharts() {
  const deviceId = queryForm.device_id || props.device?.device_id
  if (!deviceId) return
  loading.value = true
  try {
    const result = await getDeviceFlowDetailApi(deviceId)
    drawNetworkSpeedChart(result.one_hour_speed || [])
    drawFlowChart(result.one_weak_flow || [])
  } finally {
    loading.value = false
  }
}

watch(locale, () => {
  if (props.modelValue && (queryForm.device_id || props.device?.device_id)) {
    void loadCharts()
  }
})

function onDrawerOpened() {
  if (props.device?.device_id) {
    queryForm.device_id = props.device.device_id
  }
  void refreshList()
  loadCharts()
}

return (_ctx: any,_cache: any) => {
  const _component_el_tag = _resolveComponent("el-tag")!
  const _component_el_tab_pane = _resolveComponent("el-tab-pane")!
  const _component_el_tabs = _resolveComponent("el-tabs")!
  const _component_CmSvgIcon = _resolveComponent("CmSvgIcon")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _directive_loading = _resolveDirective("loading")!

  return (_openBlock(), _createBlock(_component_el_drawer, {
    "model-value": _ctx.modelValue,
    title: drawerTitle.value,
    size: 600,
    "destroy-on-close": "",
    direction: "rtl",
    "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (emit('update:modelValue', $event))),
    onOpened: onDrawerOpened
  }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("span", _hoisted_3, _toDisplayString(_ctx.$t('overviewZone.trafficRankTitle')), 1),
          _createVNode(_component_el_tag, {
            size: "small",
            type: "primary"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.$t('common.rankTagTop5')), 1)
            ]),
            _: 1
          })
        ]),
        _createVNode(_component_el_tabs, {
          modelValue: queryForm.data_type,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryForm.data_type) = $event)),
          onTabClick: changeTab
        }, {
          default: _withCtx(() => [
            _createVNode(_component_el_tab_pane, {
              label: _ctx.$t('overviewZone.trafficUpload'),
              name: 1
            }, null, 8, ["label"]),
            _createVNode(_component_el_tab_pane, {
              label: _ctx.$t('overviewZone.trafficDownload'),
              name: 2
            }, null, 8, ["label"])
          ]),
          _: 1
        }, 8, ["modelValue"]),
        _createElementVNode("div", _hoisted_4, [
          _createVNode(CmTable, {
            ref_key: "tableRef",
            ref: tableRef,
            tableOpt: tableOpt.value,
            api: _unref(getSafeMonitorFlowStatTopApi),
            modelValue: queryForm,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryForm) = $event)),
            "first-loading": false
          }, {
            num: _withCtx(({ value }) => [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_CmSvgIcon, {
                  name: "views/dashboard/manage/terminal-traffic-control/terminal-traffic-state-list/images/7",
                  monochrome: ""
                }),
                _createVNode(_component_CmSvgIcon, {
                  name: "views/dashboard/manage/terminal-traffic-control/terminal-traffic-state-list/images/6",
                  monochrome: ""
                }),
                _createElementVNode("span", null, _toDisplayString(_unref(bytesToSize)(value)), 1)
              ])
            ]),
            _: 1
          }, 8, ["tableOpt", "api", "modelValue"])
        ]),
        _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_6, [
          _createElementVNode("canvas", {
            ref_key: "networkSpeedChartEl",
            ref: networkSpeedChartEl
          }, null, 512),
          _createElementVNode("canvas", {
            ref_key: "flowChartEl",
            ref: flowChartEl
          }, null, 512)
        ])), [
          [_directive_loading, loading.value]
        ])
      ])
    ]),
    _: 1
  }, 8, ["model-value", "title"]))
}
}

})